// Pozycje faktury w edytorze i w korekcie: lustro Login/backend/lib/invoiceData.js
// (calculateInvoiceTotals). Zmiana sposobu liczenia w jednym miejscu wymaga zmiany w drugim,
// inaczej edytor pokazuje inną kwotę niż ta, którą serwer zapisze na fakturze.
//
// POZYCJE „OD BRUTTO” (12.10.2026). Kwoty rezerwacji są brutto, więc pozycje „Z rezerwacji”
// przychodzą z `unit_price_gross`: brutto pozycji jest dokładnie ilość × cena brutto, netto to
// brutto podzielone przez (1 + stawka), VAT to różnica. Dawniej edytor wpisywał kwotę brutto
// w kolumnę „Cena netto” i doliczał do niej VAT. Pozycja bez `unit_price_gross` liczy się od netto.

// STAWKA „zw” (F.15): pozycja zwolniona z VAT ma kod 'zw' i VAT 0. To nie stawka 0%: na fakturze
// i w KSeF to inna sprzedaż, więc stan pozycji niesie `vat_rate_code`, a listy, zestawienie
// i porównania idą po kluczu stawki (`kluczStawki`), nie po liczbie. Lustro
// Login/backend/lib/vatRate.js#kodStawki.
export const KOD_ZWOLNIENIA = 'zw';
export const KLUCZE_STAWEK = ['23', '8', '5', '0', KOD_ZWOLNIENIA];

/** Klucz stawki pozycji: 'zw' albo liczba jako tekst ('23', '0'); pozycja bez kodu po liczbie. */
export function kluczStawki(it) {
  const kod = it && it.vat_rate_code;
  if (kod !== null && kod !== undefined && kod !== '') return String(kod).toLowerCase();
  const n = Number(it && it.vat_rate);
  return String(Number.isFinite(n) ? n : 0);
}

/** Stawka z klucza listy: `{ vat_rate, vat_rate_code }`. */
export function stawkaZKlucza(klucz) {
  if (klucz === KOD_ZWOLNIENIA) return { vat_rate: 0, vat_rate_code: KOD_ZWOLNIENIA };
  return { vat_rate: Number(klucz), vat_rate_code: String(klucz) };
}

export const round2 = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round((x + Number.EPSILON) * 100) / 100;
};

// Symetrycznie względem zera, żeby storno pozycji dawało ten sam grosz co pozycja.
export const round2Sym = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return x < 0 ? -round2(-x) : round2(x);
};

export const nettoZBrutto = (brutto, stawka) => round2Sym(Number(brutto) / (1 + (Number(stawka) || 0) / 100));

/** Cena jednostkowa brutto pozycji albo null, gdy pozycja liczy się od netto. */
export function cenaBrutto(it) {
  const g = it && it.unit_price_gross;
  if (g === undefined || g === null || g === '') return null;
  const n = Number(g);
  return Number.isFinite(n) ? n : null;
}

export function calcItem(it) {
  const qty = Number(it.quantity) || 0;
  const vatRate = Number(it.vat_rate) || 0;
  const unitGross = cenaBrutto(it);
  if (unitGross !== null) {
    const totalGross = round2Sym(qty * unitGross);
    const totalNet = nettoZBrutto(totalGross, vatRate);
    return { ...it, total_net: totalNet, total_vat: round2Sym(totalGross - totalNet), total_gross: totalGross };
  }
  const unitNet = Number(it.unit_price_net) || 0;
  const totalNet = round2(qty * unitNet);
  const totalVat = round2(totalNet * vatRate / 100);
  return { ...it, total_net: totalNet, total_vat: totalVat, total_gross: round2(totalNet + totalVat) };
}

/**
 * Zmiana pola pozycji w edytorze. Wpisana cena netto przełącza pozycję na liczenie od netto
 * (użytkownik ustala netto sam). Zmiana stawki VAT w pozycji od brutto zostawia brutto (tyle
 * klient zapłacił) i przelicza pokazywaną cenę netto.
 */
export function zmienPozycje(it, pole, wartosc) {
  if (pole === 'unit_price_net') {
    const odNetto = { ...it, unit_price_net: wartosc };
    delete odNetto.unit_price_gross;
    return odNetto;
  }
  const nowa = { ...it, [pole]: wartosc };
  // Sama liczba nie mówi, czy to 0% czy „zw”: kod ustawia `zmienStawke`.
  if (pole === 'vat_rate') delete nowa.vat_rate_code;
  const brutto = cenaBrutto(nowa);
  if (pole === 'vat_rate' && brutto !== null) {
    nowa.unit_price_net = nettoZBrutto(brutto, wartosc);
  }
  return nowa;
}

/**
 * Zmiana stawki pozycji kluczem listy ('23', '0', 'zw'). Pozycja od brutto zostawia brutto i liczy
 * netto na nowo (jak `zmienPozycje` przy `vat_rate`).
 */
export function zmienStawke(it, klucz) {
  const { vat_rate: stawka, vat_rate_code: kod } = stawkaZKlucza(klucz);
  return { ...zmienPozycje(it, 'vat_rate', stawka), vat_rate_code: kod };
}

/** Kod stawki przeniesiony z pozycji źródłowej (tylko gdy jest). */
const zKodem = (pozycja, it) => {
  const kod = it && it.vat_rate_code;
  if (kod !== null && kod !== undefined && kod !== '') pozycja.vat_rate_code = String(kod);
  return pozycja;
};

/** Pozycja z podpowiedzi „Z rezerwacji” (GET /api/invoices/prefill/reservation/:id). */
export function pozycjaZPodpowiedzi(it, domyslnaStawka = 23) {
  const stawka = it.vat_rate === null || it.vat_rate === undefined || it.vat_rate === '' || !Number.isFinite(Number(it.vat_rate))
    ? domyslnaStawka
    : Number(it.vat_rate);
  const pozycja = {
    name: it.name,
    unit: it.unit || 'szt.',
    quantity: Number(it.quantity) || 1,
    unit_price_net: Number(it.unit_price_net) || 0,
    vat_rate: stawka,
  };
  const brutto = cenaBrutto(it);
  if (brutto !== null) pozycja.unit_price_gross = brutto;
  return zKodem(pozycja, it);
}

/**
 * Pozycja zapisanej faktury (edycja proformy). Baza nie przechowuje ceny brutto, więc pozycję,
 * której zapisanego brutto nie da się odtworzyć liczeniem od netto, edytor liczy od jej brutto:
 * inaczej sama edycja (np. zmiana daty) przesuwałaby kwotę proformy z rezerwacji o grosz.
 */
export function pozycjaZFaktury(it) {
  const pozycja = {
    name: it.name,
    unit: it.unit,
    quantity: Number(it.quantity),
    unit_price_net: Number(it.unit_price_net),
    vat_rate: Number(it.vat_rate),
  };
  const zapisaneBrutto = Number(it.total_gross);
  if (pozycja.quantity && Number.isFinite(zapisaneBrutto) && calcItem(pozycja).total_gross !== round2(zapisaneBrutto)) {
    pozycja.unit_price_gross = zapisaneBrutto / pozycja.quantity;
  }
  return zKodem(pozycja, it);
}

/** Pozycja w ciele POST/PUT /api/invoices. */
export function pozycjaDoZapisu(it) {
  const pozycja = {
    name: it.name,
    unit: it.unit || 'szt.',
    quantity: Number(it.quantity) || 0,
    unit_price_net: Number(it.unit_price_net) || 0,
    vat_rate: Number(it.vat_rate) || 0,
  };
  const brutto = cenaBrutto(it);
  if (brutto !== null) pozycja.unit_price_gross = brutto;
  return zKodem(pozycja, it);
}

/**
 * Pozycja korekty-storna. Brutto oryginału z minusem: storno zeruje każdą pozycję co do grosza,
 * także pozycję policzoną od brutto (od netto korekta wychodziła o grosz inna niż faktura).
 * Kod stawki oryginału przechodzi bez zmian (storno pozycji „zw” jest „zw”, F.15).
 */
export function pozycjaKorekty(it) {
  const ilosc = Number(it.quantity);
  const pozycja = {
    name: `KOREKTA: ${it.name}`,
    unit: it.unit,
    quantity: it.quantity,
    unit_price_net: -Number(it.unit_price_net),
    vat_rate: it.vat_rate,
  };
  const brutto = Number(it.total_gross);
  if (ilosc && Number.isFinite(brutto)) pozycja.unit_price_gross = -brutto / ilosc;
  return zKodem(pozycja, it);
}
